import React, { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { createCheckoutSession } from '../../services/api'
import { toast } from 'react-toastify'
import './PurchaseCreditsModal.scss'

interface PurchaseCreditsModalProps {
  show: boolean
  onHide: () => void
}

function PurchaseCreditsModal({ show, onHide }: PurchaseCreditsModalProps) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({})

  const handleBuyCredits = async (creditPackage: 1 | 5 | 10) => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase credits')
      return
    }

    setLoading({ ...loading, [creditPackage]: true })

    try {
      const { url } = await createCheckoutSession(creditPackage, getAccessTokenSilently)
      
      // Redirect to Stripe Checkout
      if (url) {
        window.location.href = url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (error) {
      logger.error('Error creating checkout session', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error('Failed to start checkout. Please try again.')
      setLoading({ ...loading, [creditPackage]: false })
    }
  }

  if (!show) return null

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
    >
      <div 
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h2 className="modal-title w-100 text-center fw-bold mb-0" style={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
              fontSize: '28px'
            }}>
              Buy Credits
            </h2>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onHide}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body px-4 pb-4">
            <div className="mb-4">
              <h3 
                className="fw-bold mb-2 purchase-modal-heading"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                  fontSize: '24px'
                }}
              >
                Need More?
              </h3>
              <p 
                className="text-muted mb-4 purchase-modal-description"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              >
                Buy additional credits when you need them. No subscription required.
              </p>
            </div>

            {/* Single Credit Option */}
            <div className="border rounded-3 p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h4 
                    className="fw-bold mb-1 purchase-modal-plan-title"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '18px'
                    }}
                  >
                    1 Credit
                  </h4>
                  <p 
                    className="text-muted mb-0 small purchase-modal-plan-description"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    Resume + Interview Prep for 1 job
                  </p>
                </div>
                <div className="text-end">
                  <div 
                    className="fw-bold purchase-modal-price"
                    style={{
                      fontSize: '24px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                    }}
                  >
                    $5
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleBuyCredits(1)}
                className="btn btn-outline-primary btn-sm w-100"
                disabled={loading[1]}
              >
                {loading[1] ? 'Loading...' : 'Buy 1 Credit'}
              </button>
            </div>

            {/* 5 Credits Option */}
            <div className="border rounded-3 p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h4 
                    className="fw-bold mb-1 purchase-modal-plan-title"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '18px'
                    }}
                  >
                    5 Credits
                  </h4>
                  <p 
                    className="text-muted mb-0 small purchase-modal-plan-description"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    Resume + Interview Prep for 5 jobs
                  </p>
                  <p 
                    className="text-success mb-0 small fw-bold mt-1"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '12px'
                    }}
                  >
                    Save $5 (20% off)
                  </p>
                </div>
                <div className="text-end">
                  <div 
                    className="fw-bold purchase-modal-price"
                    style={{
                      fontSize: '24px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                    }}
                  >
                    $20
                  </div>
                  <div 
                    className="text-muted small text-decoration-line-through purchase-modal-old-price"
                    style={{ fontSize: '14px' }}
                  >
                    $25
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleBuyCredits(5)}
                className="btn btn-outline-primary btn-sm w-100"
                disabled={loading[5]}
              >
                {loading[5] ? 'Loading...' : 'Buy 5 Credits'}
              </button>
            </div>

            {/* 10 Credits Option - Best Value */}
            <div className="border border-primary rounded-3 p-3 position-relative" style={{ borderWidth: '2px' }}>
              <div className="position-absolute top-0 end-0 m-2">
                <span className="badge bg-success text-uppercase px-2 py-1" style={{ fontSize: '11px' }}>
                  Best Value
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2" style={{ marginTop: '24px' }}>
                <div>
                  <h4 
                    className="fw-bold mb-1 purchase-modal-plan-title"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '18px'
                    }}
                  >
                    10 Credits
                  </h4>
                  <p 
                    className="text-muted mb-0 small purchase-modal-plan-description"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    Resume + Interview Prep for 10 jobs
                  </p>
                  <p 
                    className="text-success mb-0 small fw-bold mt-1"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '12px'
                    }}
                  >
                    Save $15 (30% off)
                  </p>
                </div>
                <div className="text-end">
                  <div 
                    className="fw-bold purchase-modal-price purchase-modal-price-primary"
                    style={{
                      fontSize: '24px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                    }}
                  >
                    $35
                  </div>
                  <div 
                    className="text-muted small text-decoration-line-through purchase-modal-old-price"
                    style={{ fontSize: '14px' }}
                  >
                    $50
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleBuyCredits(10)}
                className="btn btn-primary btn-sm w-100"
                disabled={loading[10]}
              >
                {loading[10] ? 'Loading...' : 'Buy 10 Credits'}
              </button>
            </div>

            <p 
              className="text-muted small text-center mt-3 mb-0 purchase-modal-footer"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '12px'
              }}
            >
              Credits never expire. Use them whenever you need.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseCreditsModal

